import React, { useEffect, useRef, useState } from 'react';
import { ChartRef } from '../_config/interface';
import { findLastIndex } from "lodash-es";

import './RadarPoint.scss';

interface IPointState {
  left: number;
  top: number;
  color: string;
  visible: boolean;
}

interface IRadarPointProps {
  chartRef: React.RefObject<ChartRef>;
  option: any;
  /** 是否对所有 series 各自渲染末端动效点，默认 false（向后兼容，只渲染第一条线） */
  multiLine?: boolean;
}

/** 单条线末端动效点（原有逻辑，向后兼容） */
const SingleRadarPoint: React.FC<{ chartRef: React.RefObject<ChartRef>; option: any; }> = ({ chartRef, option }) => {
  const [position, setPosition] = useState<{left: number; top: number;}>({left: 0, top: 0});
  const [showPoint, setShowPoint] = useState<boolean>(true);

  useEffect(() => {
    // 获取第一条线
    const firstLine = option?.series?.[0];
    // 获取第一条线的最后一个点的X坐标
    const lastX = findLastIndex(firstLine?.data, (item: any) => item);

    if (lastX < 0) {
      return;
    }
    // 获取第一条线的最后一个点的Y坐标
    const lastY = Number(firstLine?.data?.[lastX]?.value);
    // 获取chat实例
    const instance = chartRef?.current?.instance();

    // 更新坐标
    const updatePostion = (params?: any) => {
      const { type, name, selected } = params || {};
      if (type === 'legendselectchanged' && name === firstLine?.name ) {
        // 图例影藏直接返回
        if (!selected?.[name]) {
          setShowPoint(false);
          return;
        }
        setShowPoint(true);
      }
      // 获取最后一个点的坐标，相对于左上角
      const [left = 0, top = 0] = instance?.convertToPixel({xAxisIndex: 0, yAxisIndex: 0}, [lastX, lastY]) || [];
      // 判定最后一个点是否在坐标系内，dataZoom 开启时可能会在坐标系外
      const isInChart = instance?.containPixel({gridIndex: 0}, [left, top]);
      // console.log("params", params, lastX, left, top, isInChart);
      if (!isInChart) {
        setPosition({
          left: 0,
          top: 0
        });
        return;
      }
      setPosition({
        left: left || 0,
        top: top || 0
      });
      // console.log('RadarPoint useEffect point', lastX, lastY, lastPosition);
    };

    updatePostion();

    // dataZoom 事件
    instance?.on('dataZoom', updatePostion);
    // resize 事件
    instance?.on('finished', updatePostion);
    // legendselectchanged 事件,图例选中获取取消
    instance?.on('legendselectchanged', updatePostion);

    return () => {
      instance?.off('dataZoom', updatePostion);
      instance?.off('finished', updatePostion);
      instance?.off('legendselectchanged', updatePostion);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option]);

  const { left, top } = position;
  const show = showPoint && (left || top);

  return <div className='dbd-radar-point-wrap' style={{left: `${left}px`, top: `${top}px`, visibility: show ? 'visible' : 'hidden'}}>
    <span style={{animationDelay: '0s'}}></span>
    <span style={{animationDelay: '1s'}}></span>
    <span style={{animationDelay: '2s'}}></span>
  </div>;
};

interface IColoredRadarDotProps {
  point: IPointState;
}

/** 单个彩色动效点，通过 ref 设置 CSS 变量确保颜色正确传递 */
const ColoredRadarDot: React.FC<IColoredRadarDotProps> = ({ point }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.style.setProperty('--radar-point-color', point.color);
    }
  }, [point.color]);

  return (
    <div
      ref={ref}
      className={`dbd-radar-point-wrap dbd-radar-point-wrap--colored${point.visible ? '' : ' dbd-radar-point-wrap--hidden'}`}
      style={{
        left: `${point.left}px`,
        top: `${point.top}px`,
      }}
    >
      <span style={{animationDelay: '0s'}}></span>
      <span style={{animationDelay: '1s'}}></span>
      <span style={{animationDelay: '2s'}}></span>
    </div>
  );
};

/** 多条线末端动效点 */
const MultiRadarPoint: React.FC<{ chartRef: React.RefObject<ChartRef>; option: any; }> = ({ chartRef, option }) => {
  const [points, setPoints] = useState<IPointState[]>([]);
  const legendSelectedRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    const colorPalette: string[] = option?.color || [];

    /** 从数据点中提取有效数值（兼容纯数值和 {value,...} 对象格式） */
    const getItemValue = (item: any): number | null => {
      if (item === null || item === undefined || item === '') return null;
      const val = typeof item === 'object' ? item?.value : item;
      if (val === null || val === undefined || val === '') return null;
      return Number(val);
    };

    const computePoints = (legendSelected?: Record<string, boolean>): void => {
      const instance = chartRef?.current?.instance();
      if (!instance) return;

      const seriesList = (option?.series || []).filter((s: any) => s?.type === 'line');

      const nextPoints: IPointState[] = seriesList.map((seriesItem: any, idx: number) => {
        // 优先读 series 上直接写入的 color，其次按顺序取调色盘
        const color: string = seriesItem?.color || colorPalette[idx % colorPalette.length] || '#5086F3';

        const data: any[] = seriesItem?.data || [];
        const lastX = findLastIndex(data, (item: any) => getItemValue(item) !== null);

        if (lastX < 0) {
          return { left: 0, top: 0, color, visible: false };
        }

        const lastY = getItemValue(data[lastX]) ?? 0;

        // 图例隐藏时不显示
        if (seriesItem?.name && legendSelected?.[seriesItem.name] === false) {
          return { left: 0, top: 0, color, visible: false };
        }

        const [left = 0, top = 0] = instance.convertToPixel({xAxisIndex: 0, yAxisIndex: 0}, [lastX, lastY]) || [];
        const isInChart = instance.containPixel({gridIndex: 0}, [left, top]);

        return {
          left: isInChart ? (left || 0) : 0,
          top: isInChart ? (top || 0) : 0,
          color,
          visible: !!isInChart && (!!left || !!top),
        };
      });
      setPoints(nextPoints);
    };

    const handleEvent = (params?: any): void => {
      if (params?.type === 'legendselectchanged') {
        // 持久化图例状态，避免后续 finished/dataZoom 事件覆盖
        legendSelectedRef.current = params?.selected || {};
      }
      computePoints(legendSelectedRef.current);
    };

    const instance = chartRef?.current?.instance();
    instance?.on('finished', handleEvent);
    instance?.on('dataZoom', handleEvent);
    instance?.on('legendselectchanged', handleEvent);
    handleEvent();

    return () => {
      instance?.off('finished', handleEvent);
      instance?.off('dataZoom', handleEvent);
      instance?.off('legendselectchanged', handleEvent);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option]);

  return (
    <>
      {points.map((pt, idx) => (
        <ColoredRadarDot key={idx} point={pt} />
      ))}
    </>
  );
};

const RadarPoint: React.FC<IRadarPointProps> = (props) => {
  const { chartRef, option, multiLine = false } = props;

  if (multiLine) {
    return <MultiRadarPoint chartRef={chartRef} option={option} />;
  }

  return <SingleRadarPoint chartRef={chartRef} option={option} />;
};

export default RadarPoint;
